import React, { Suspense, lazy, useEffect } from 'react';
import { useForceRender } from 'src/hooks/useForceRender';

import { Image } from '@lt/components';
import SideNavMenu from 'src/components/Header/components/HeaderMenu/components/SideNavMenu';

import HeaderWriteUs from 'src/components/Header/components/HeaderMenu/components/HeaderWriteUs';
import { useSidebarOpener } from 'src/components/Layout/hooks/useSidebarOpener';
import Auth from 'src/routes/Mobile';
import BannerController from 'src/components/Banners';
import Footer from 'src/components/Footer';
import { useTranslation } from '@lt/localization';
import { isSSR } from 'src/helpers/ssr';
import { isPartner } from 'src/helpers/isPartner';

import CountriesList from '../CountriesList';
import Header from '../Header';
import Relinking from '../Relinking';
import SearchForm from '../SearchForm';
import DynamicDataWrapper from './components/DynamicDataWrapper';
import HomeLtBottomPromo from './components/HomeLtBottomPromo';

import type { MobileSearchBlockProps } from './types';
import styles from './mobileSearchBlock.module.css';
import { NpsState } from '../NpsForm/NpsForm';
import LTPremiumImageWebp from '../../../../images/lt_premium_bg.webp';
import LTPremiumImageJpg from '../../../../images/lt_premium_bg.jpg';

const NpsForm = lazy(
  () => import(/* webpackChunkName: "NpsForm" */ '../NpsForm'),
);

const MobileSearchBlock = ({
  isWhitelabel,
  isWebView,
  isLoggedIn,
  onLogOut,
  partnerLogo,
  onShowAuthPopup,
  npsState,
  backgroundAlphaImage,
}: MobileSearchBlockProps): JSX.Element => {
  const { t } = useTranslation();

  const { isSidebarOpened, openSidebar, closeSidebar } = useSidebarOpener();
  const { countForceRender } = useForceRender();

  useEffect(() => {
    if (isPartner('tbank'))
      window.parent.postMessage(
        { name: 'frameRenderSuccess', success: true },
        '*',
      );
  }, []);

  let src: string | undefined;
  let fallback: string;

  if (isPartner('alfa')) {
    src = backgroundAlphaImage;
    fallback = 'https://img.cdn.level.travel/assets/main-alpha.jpg';
  } else if (isPartner('premium')) {
    src = LTPremiumImageWebp;
    fallback = LTPremiumImageJpg;
  } else {
    src = 'https://img.cdn.level.travel/assets/wl_header_background.webp';
    fallback = 'https://img.cdn.level.travel/assets/wl_header_background.jpg';
  }

  return (
    <>
      {!isSSR && <BannerController />}
      <section className={styles.sectionView}>
        {isSidebarOpened && (
          <SideNavMenu
            onShowAuthPopup={onShowAuthPopup}
            onLogOut={onLogOut}
            closeSidebar={closeSidebar}
            isWhitelabel={isWhitelabel}
            isLoggedIn={isLoggedIn}
          />
        )}
        <div className={styles.topGradient} />
        <div className={styles.bottomGradient} />

        <Image
          className={styles.image}
          src={src}
          fallback={fallback}
          alt="preview image"
        />
        {!isPartner('premium') && (
          <div className={styles.textContainer}>
            <div className={styles.textHeader}>
              <span>
                {isPartner('alfa')
                  ? t('homeHeader.promoTitleAlpha')
                  : t('homeHeader.promoTitle')}
              </span>
            </div>
            <div className={styles.text}>
              <span>
                {isPartner('alfa')
                  ? t('homeHeader.promoTextAlpha')
                  : t('homeHeader.promoText')}
              </span>
            </div>
          </div>
        )}

        {!isSSR && npsState !== NpsState.Hide && (
          <Suspense fallback={<div />}>
            <NpsForm npsState={npsState} />
          </Suspense>
        )}

        {!isWebView && (
          <Header
            redirectLogoLink="/"
            onBurgerClick={openSidebar}
            partnerLogo={partnerLogo}
            isWhitelabel={isWhitelabel}
          />
        )}
        {!isSSR && (
          <DynamicDataWrapper>
            <SearchForm />
          </DynamicDataWrapper>
        )}
      </section>
      {!isWebView && (
        <>
          <Relinking isMobile />
          <HeaderWriteUs />
        </>
      )}
      {!isSSR && <Auth />}
      {!isWhitelabel && (
        <>
          <HomeLtBottomPromo isMobile />
          <CountriesList isMobile />
        </>
      )}
      {!isWhitelabel && <Footer key={countForceRender} />}
    </>
  );
};

export default MobileSearchBlock;
